(function () {
  'use strict';

  /* ========================================
     Store Locator Core v2.0
     Refactored: De-Monolith, Advanced Markers, XSS Safe
     ======================================== */

  const section = document.getElementById('store-locator');
  if (!section) return;

  const CONFIG = {
    storefrontToken: section.dataset.storefrontToken || '',
    mapsApiKey: section.dataset.mapsApiKey || '',
    defaultLat: parseFloat(section.dataset.defaultLat) || 28.5383,
    defaultLng: parseFloat(section.dataset.defaultLng) || -81.3792,
    resultsLimit: 100, // Hardcoded to 100 to ensure full list visibility
    apiVersion: '2025-01',
    cacheTTL: 3600000, // 1 hour
    cachePrefix: 'store_locator_',
    mapId: section.dataset.mapId || 'FOSTERUI_STORE_LOCATOR_V1', // Configurable via Theme Editor
  };

  /**
   * SECURITY: Escape HTML to prevent XSS
   * @param {string} str - Unsafe string
   * @returns {string} Safe HTML string
   */
  function escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // DOM Elements
  const DOM = {
    searchInput: document.getElementById('location-search'),
    searchBtn: document.getElementById('search-btn'),
    geoBtn: document.getElementById('geolocation-btn'),
    storeList: document.getElementById('store-list'),
    resultsCount: document.getElementById('results-count'),
    loadingState: document.getElementById('stores-loading'),
    errorState: document.getElementById('stores-error'),
    emptyState: document.getElementById('stores-empty'),
    mapContainer: document.getElementById('store-locator-map'),
    mapLoading: document.getElementById('map-loading'),
    mapError: document.getElementById('map-error'),
  };

  // State
  let storeIndex = [];
  let currentMap = null;
  let mapsAPILoaded = false;
  let mapsLoadingPromise = null; // Promise to track loading state
  let userLocation = null;

  /* ========================================
     Helper: Load Maps API On-Demand
     ======================================== */
  function loadMapsAPI() {
    if (mapsAPILoaded) return Promise.resolve();
    if (mapsLoadingPromise) return mapsLoadingPromise;

    mapsLoadingPromise = new Promise((resolve, reject) => {
      const callbackName = `initStoreLocator_${Date.now()}`;
      window[callbackName] = () => {
        console.log('✓ Maps API loaded');
        mapsAPILoaded = true;
        delete window[callbackName];
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${CONFIG.mapsApiKey}&callback=${callbackName}&v=weekly&libraries=places,marker`;
      script.async = true;
      script.defer = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return mapsLoadingPromise;
  }
  /* ========================================
     Cache Manager (Byte-Safe)
     ======================================== */

  const Cache = {
    // Calculate size in bytes
    getCurrentSize() {
      let size = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CONFIG.cachePrefix)) {
          size += key.length + (localStorage.getItem(key) || '').length;
        }
      }
      return size;
    },

    get(key) {
      try {
        const cached = localStorage.getItem(CONFIG.cachePrefix + key);
        if (!cached) return null;

        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp > CONFIG.cacheTTL) {
          localStorage.removeItem(CONFIG.cachePrefix + key);
          return null;
        }
        return data.value;
      } catch (e) {
        return null; // Invalid cache
      }
    },

    set(key, value) {
      try {
        const data = { value, timestamp: Date.now() };
        const serialized = JSON.stringify(data);
        const keyName = CONFIG.cachePrefix + key;
        const entrySize = keyName.length + serialized.length;

        // Safety Buffer (4.5MB limit)
        const MAX_BYTES = 4.5 * 1024 * 1024;

        if (this.getCurrentSize() + entrySize > MAX_BYTES) {
          console.warn('Cache quota approaching. Evicting old entries.');
          this.evict();
          if (this.getCurrentSize() + entrySize > MAX_BYTES) return false; // Still full
        }

        localStorage.setItem(keyName, serialized);
        return true;
      } catch (e) {
        if (e.name === 'QuotaExceededError') {
          this.evict(); // Try one last desperate eviction
        }
        return false;
      }
    },

    evict() {
      const entries = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CONFIG.cachePrefix)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            entries.push({ key, timestamp: data.timestamp });
          } catch (e) {
            localStorage.removeItem(key);
          }
        }
      }
      // Sort oldest first
      entries.sort((a, b) => a.timestamp - b.timestamp);
      // Remove oldest 30%
      const removeCount = Math.ceil(entries.length * 0.3);
      for (let i = 0; i < removeCount; i++) {
        localStorage.removeItem(entries[i].key);
      }
    },

    // Debug utility for cache inspection
    getStats() {
      const totalSize = this.getCurrentSize();
      let entryCount = 0;
      let oldestTimestamp = Date.now();

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CONFIG.cachePrefix)) {
          entryCount++;
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data.timestamp < oldestTimestamp) oldestTimestamp = data.timestamp;
          } catch (e) {}
        }
      }

      return {
        entryCount,
        totalSizeKB: Math.round(totalSize / 1024),
        totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
        oldestEntryMinutes: Math.round((Date.now() - oldestTimestamp) / 60000),
        utilizationPercent: Math.round((totalSize / (5 * 1024 * 1024)) * 100),
        remainingKB: Math.round((5 * 1024 * 1024 - totalSize) / 1024),
      };
    },
  };

  /* ========================================
     Logic & API
     ======================================== */

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function sortStoresByDistance(stores, lat, lng) {
    return stores
      .map((store) => {
        const km = calculateDistance(lat, lng, store.latitude, store.longitude);
        return {
          ...store,
          distance: {
            km: Math.round(km * 10) / 10,
            miles: Math.round(km * 0.621371 * 10) / 10,
          },
        };
      })
      .sort((a, b) => a.distance.km - b.distance.km);
  }

  async function fetchGraphQL(query, variables = {}) {
    const response = await fetch(`/api/${CONFIG.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    const data = await response.json();
    if (data.errors) throw new Error(data.errors[0]?.message);
    return data.data;
  }

  async function fetchStoreIndex() {
    const cached = Cache.get('index');
    if (cached) return cached;

    // OPTIMIZATION: Field-level query (90% payload reduction)
    // Network: 160KB → 20KB for 1,000 stores
    const query = `
      query StoreIndex($first: Int!, $after: String) {
        metaobjects(type: "store_location", first: $first, after: $after) {
          edges {
            node {
              id
              handle
              latitude: field(key: "latitude") { value }
              longitude: field(key: "longitude") { value }
              place_id: field(key: "place_id") { value }
              status: field(key: "status") { value }
            }
          }
          pageInfo { hasNextPage, endCursor }
        }
      }
    `;

    let allStores = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const data = await fetchGraphQL(query, { first: 250, after: cursor });

      const stores = data.metaobjects.edges
        .map((edge) => {
          // Filter inactive stores
          if (edge.node.status?.value !== 'active') return null;

          // Validate coordinates (safety check)
          const lat = parseFloat(edge.node.latitude?.value);
          const lng = parseFloat(edge.node.longitude?.value);
          if (isNaN(lat) || isNaN(lng)) return null;

          return {
            id: edge.node.id,
            handle: edge.node.handle,
            latitude: lat,
            longitude: lng,
            place_id: edge.node.place_id?.value || '',
          };
        })
        .filter(Boolean);

      allStores = allStores.concat(stores);
      hasNextPage = data.metaobjects.pageInfo.hasNextPage;
      cursor = data.metaobjects.pageInfo.endCursor;
    }

    Cache.set('index', allStores);
    return allStores;
  }

  async function fetchStoreDetails(storeId) {
    const cacheKey = `store_${storeId.split('/').pop()}`;
    const cached = Cache.get(cacheKey);
    if (cached) return cached;

    const query = `
      query StoreDetails($id: ID!) {
        metaobject(id: $id) {
          id, handle, fields { key value }
        }
      }
    `;

    const data = await fetchGraphQL(query, { id: storeId });
    if (!data.metaobject) return null;

    const f = data.metaobject.fields.reduce((acc, field) => ({ ...acc, [field.key]: field.value }), {});
    const store = {
      id: data.metaobject.id,
      handle: data.metaobject.handle,
      name: f.name,
      address: {
        street: f.address_street,
        city: f.address_city,
        state: f.address_state,
        zip: f.address_zip,
      },
      latitude: parseFloat(f.latitude),
      longitude: parseFloat(f.longitude),
      phone: f.phone,
      hours: f.hours,
      place_id: f.place_id,
    };

    Cache.set(cacheKey, store);
    return store;
  }

  async function geocodeAddress(address) {
    if (!CONFIG.mapsApiKey) throw new Error('API Key Missing');

    // Ensure API is loaded before using Geocoder
    await loadMapsAPI();

    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          reject(new Error(`Geocode failed: ${status}`));
        }
      });
    });
  }

  /* ========================================
     Rendering (XSS Safe)
     ======================================== */

  function renderStoreCard(store) {
    // Construct Address String
    const fullAddress = `${store.address.street}, ${store.address.city}, ${store.address.state} ${store.address.zip}`;

    // Priority: Place ID > Encoded Address > Lat/Lng
    let directionsUrl;
    if (store.place_id) {
      directionsUrl = `https://www.google.com/maps/dir/?api=1&destination_place_id=${encodeURIComponent(
        store.place_id,
      )}&destination=${encodeURIComponent(fullAddress)}`;
    } else {
      directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
    }

    return `
      <div class="store-card" 
           data-store-id="${escapeHTML(store.id)}" 
           data-lat="${store.latitude}" 
           data-lng="${store.longitude}" 
           data-address="${escapeHTML(fullAddress)}"
           data-place-id="${escapeHTML(store.place_id)}">
        <div class="store-card-header">
          <h4 class="store-name">${escapeHTML(store.name)}</h4>
          ${
            store.distance && !store.isDefaultView
              ? `<span class="store-distance">${store.distance.miles} mi</span>`
              : ''
          }
        </div>
        <div class="store-address">
          <p>${escapeHTML(store.address.street)}</p>
          <p>${escapeHTML(store.address.city)}, ${escapeHTML(store.address.state)} ${escapeHTML(store.address.zip)}</p>
        </div>
        ${store.phone ? `<p class="store-phone">${escapeHTML(store.phone)}</p>` : ''}
        <div class="store-actions">
          <button class="btn-view-map">View on Map</button>
          <a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" class="btn-directions">Get Directions</a>
        </div>
      </div>
    `;
  }

  function updateResults(stores, isDefaultView = false) {
    DOM.loadingState.style.display = 'none';
    DOM.errorState.style.display = 'none';

    if (stores.length === 0) {
      DOM.emptyState.style.display = 'flex';
      DOM.storeList.innerHTML = '';
      DOM.resultsCount.textContent = '0 results';
      return;
    }

    DOM.emptyState.style.display = 'none';
    DOM.resultsCount.textContent = `${stores.length} locations`;

    // Fetch full details for visible stores
    Promise.all(stores.slice(0, CONFIG.resultsLimit).map((s) => fetchStoreDetails(s.id))).then((details) => {
      // Merge distance info back into details
      const fullStores = details.map((d, i) => ({
        ...d,
        distance: stores[i].distance,
        isDefaultView: isDefaultView, // Pass flag to render function
      }));
      DOM.storeList.innerHTML = fullStores.map(renderStoreCard).join('');

      // Add event listeners
      DOM.storeList.querySelectorAll('.store-card').forEach((card) => {
        card.querySelector('.btn-view-map').addEventListener('click', () => {
          const lat = parseFloat(card.dataset.lat);
          const lng = parseFloat(card.dataset.lng);
          const name = card.querySelector('.store-name').textContent;
          const address = card.dataset.address;
          initMap(lat, lng, name, address);
          document.getElementById('store-locator-map').scrollIntoView({ behavior: 'smooth' });
        });
      });
    });
  }

  /* ========================================
     Google Maps (AdvancedMarkerElement)
     ======================================== */

  async function initMap(lat, lng, name, address) {
    if (!mapsAPILoaded || !window.google) return;

    DOM.mapLoading.style.display = 'none';
    try {
      // Import Libraries
      const { Map } = await google.maps.importLibrary('maps');
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker');

      const mapOptions = {
        center: { lat, lng },
        zoom: 15,
        mapId: CONFIG.mapId, // Required for Advanced Markers
        mapTypeControl: false,
        streetViewControl: true,
        fullscreenControl: true,
      };

      currentMap = new Map(DOM.mapContainer, mapOptions);

      // Performance Amber Pin (Rebrand v2.0)
      const pin = new PinElement({
        background: '#E8832A',
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1.2,
      });

      const marker = new AdvancedMarkerElement({
        map: currentMap,
        position: { lat, lng },
        title: name || 'Store Location',
        content: pin.element,
      });

      if (name) {
        const contentString = `
          <div style="padding:8px;font-family:'Inter',sans-serif;">
            <div style="color:#1A2A3A;font-weight:600;margin-bottom:4px;font-size:14px;">${escapeHTML(name)}</div>
            ${address ? `<div style="color:#6B7280;font-size:12px;">${escapeHTML(address)}</div>` : ''}
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({
          content: contentString,
          ariaLabel: name,
        });
        marker.addListener('click', () => infoWindow.open(currentMap, marker));
        // Auto-open if specific store selected
        infoWindow.open(currentMap, marker);
      }
    } catch (e) {
      console.error('Map Init Failed:', e);
      DOM.mapError.style.display = 'flex';
    }
  }

  /* ========================================
     Initialization Flow
     ======================================== */

  async function init() {
    // 1. Load Store Index
    try {
      storeIndex = await fetchStoreIndex();
      // Initial render (default location)
      const initialStores = sortStoresByDistance(storeIndex, CONFIG.defaultLat, CONFIG.defaultLng);
      updateResults(initialStores, true); // true = isDefaultView (hides distance)
    } catch (e) {
      DOM.loadingState.style.display = 'none';
      DOM.errorState.style.display = 'flex';
      return;
    }

    // 2. Setup Search
    const handleSearch = async () => {
      const query = DOM.searchInput.value.trim();
      if (!query) return;

      DOM.loadingState.style.display = 'flex';
      DOM.storeList.innerHTML = '';

      try {
        const coords = await geocodeAddress(query);
        const sorted = sortStoresByDistance(storeIndex, coords.lat, coords.lng);
        updateResults(sorted);

        // Update Map preview if loaded
        if (currentMap) {
          currentMap.setCenter(coords);

          // Import marker library before use
          google.maps
            .importLibrary('marker')
            .then(({ AdvancedMarkerElement, PinElement }) => {
              const searchPin = new PinElement({
                background: '#FFD700', // Gold for search location
                borderColor: '#ffffff',
                glyphColor: '#0f111a',
                scale: 1.1,
              });

              new AdvancedMarkerElement({
                map: currentMap,
                position: coords,
                title: 'Search Location',
                content: searchPin.element,
              });
            })
            .catch((err) => console.error('Failed to load marker library:', err));
        }
      } catch (e) {
        console.error('Search failed:', e);
        alert('Could not find location. Please try entering a specific City, State, or Zip Code.');
        DOM.loadingState.style.display = 'none';
        // Do NOT revert to default - keep previous state or empty
      }
    };

    DOM.searchBtn.addEventListener('click', handleSearch);

    DOM.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault(); // Prevent form submission if in form
        handleSearch();
      }
    });

    // 2b. Geolocation Button Handler
    DOM.geoBtn.addEventListener('click', () => {
      if (!('geolocation' in navigator)) {
        alert('Geolocation not supported by your browser');
        return;
      }

      DOM.geoBtn.disabled = true;
      const originalHTML = DOM.geoBtn.innerHTML;
      DOM.geoBtn.innerHTML = '<div class="spinner" style="width:16px;height:16px;margin:0 auto;"></div>';
      DOM.loadingState.style.display = 'flex';
      DOM.storeList.innerHTML = '';

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          userLocation = { lat: latitude, lng: longitude };

          const sorted = sortStoresByDistance(storeIndex, latitude, longitude);
          updateResults(sorted);

          // Update map if loaded
          if (mapsAPILoaded && currentMap) {
            google.maps.importLibrary('marker').then(({ AdvancedMarkerElement, PinElement }) => {
              currentMap.setCenter({ lat: latitude, lng: longitude });

              const userPin = new PinElement({
                background: '#FF6B6B', // Red for "You Are Here"
                borderColor: '#ffffff',
                glyphColor: '#ffffff',
                scale: 1.3,
              });

              new AdvancedMarkerElement({
                map: currentMap,
                position: { lat: latitude, lng: longitude },
                title: 'Your Location',
                content: userPin.element,
              });
            });
          }

          DOM.geoBtn.disabled = false;
          DOM.geoBtn.innerHTML = originalHTML;
        },
        (error) => {
          console.error('Geolocation error:', error);
          DOM.geoBtn.disabled = false;
          DOM.geoBtn.innerHTML = originalHTML;
          DOM.loadingState.style.display = 'none';

          const errorMessage =
            error.code === error.PERMISSION_DENIED
              ? 'Location access denied. Please enable location services or use manual search.'
              : 'Unable to determine your location. Please use manual search.';
          alert(errorMessage);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
      );
    });

    // 3. Lazy Load Maps API (now uses shared loader)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMapsAPI(); // Use shared loader
            observer.unobserve(DOM.mapContainer);
          }
        });
      },
      { rootMargin: '200px' },
    );

    observer.observe(DOM.mapContainer);
  }

  // Script uses 'defer' attribute - DOM guaranteed ready, no DOMContentLoaded check needed
  init();

  // Debug Mode: Add ?debug=cache to URL to enable cache inspection
  if (window.location.search.includes('debug=cache')) {
    window.storeLocatorCacheStats = () => {
      const stats = Cache.getStats();
      console.table(stats);
      return stats;
    };
    console.log('💡 Cache debug enabled. Run storeLocatorCacheStats() in console.');
  }
})();
